import { BrandLogo, Switch, useLegalLinks } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAppUpdater } from '@affine/core/components/hooks/use-app-updater';
import { TelemetryConsentSettings } from '@affine/core/components/telemetry-consent';
import { FetchService } from '@affine/core/modules/cloud';
import { HelpActionsService } from '@affine/core/modules/help-actions';
import { UrlService } from '@affine/core/modules/url';
import { appNames } from '@affine/core/utils/channel';
import { useI18n } from '@affine/i18n';
import { ArrowRightSmallIcon, OpenInNewIcon } from '@blocksuite/icons/rc';
import { useServices } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { relatedLinks } from './config';
import * as styles from './style.css';
import { UpdateCheckSection } from './update-check-section';
import {
  type AboutVersionSource,
  getAboutVersionDetails,
  parseAboutVersionResponse,
} from './version-details';

export const AboutAffine = ({
  versionSource,
}: {
  versionSource?: AboutVersionSource;
}) => {
  const t = useI18n();
  const legalLinks = useLegalLinks();
  const { autoCheck, autoDownload, toggleAutoCheck, toggleAutoDownload } =
    useAppUpdater();
  const channel = BUILD_CONFIG.appBuildType;
  const appName = appNames[channel];
  const { fetchService, urlService, helpActionsService } = useServices({
    FetchService,
    UrlService,
    HelpActionsService,
  });
  const [runtimeVersionSource, setRuntimeVersionSource] =
    useState<AboutVersionSource>({
      releaseVersion: null,
      buildCommit: null,
    });
  const effectiveVersionSource =
    versionSource ?? (BUILD_CONFIG.isWeb ? runtimeVersionSource : undefined);
  const versionDetails = getAboutVersionDetails(
    effectiveVersionSource,
    t.language?.startsWith('en') ? 'en' : 'de'
  );

  useEffect(() => {
    if (!BUILD_CONFIG.isWeb || versionSource) return;
    const controller = new AbortController();
    void fetchService
      .fetch('/api/version', {
        cache: 'no-store',
        signal: controller.signal,
      })
      .then(response => response.json())
      .then(value => {
        if (!controller.signal.aborted) {
          setRuntimeVersionSource(parseAboutVersionResponse(value));
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setRuntimeVersionSource({ releaseVersion: null, buildCommit: null });
        }
      });
    return () => controller.abort();
  }, [fetchService, versionSource]);

  const onSwitchAutoCheck = useCallback(
    (checked: boolean) => {
      toggleAutoCheck(checked);
    },
    [toggleAutoCheck]
  );

  const onSwitchAutoDownload = useCallback(
    (checked: boolean) => {
      toggleAutoDownload(checked);
    },
    [toggleAutoDownload]
  );

  return (
    <>
      <SettingHeader
        title={t['com.streamlain.aboutAFFiNE.title']()}
        subtitle={t['com.streamlain.aboutAFFiNE.subtitle']()}
        data-testid="about-title"
      />
      <SettingWrapper title={t['com.streamlain.aboutAFFiNE.version.title']()}>
        <SettingRow
          name={appName}
          desc={versionDetails.releaseVersion}
          className={styles.appImageRow}
        >
          <BrandLogo size={56} aria-label={appName} />
        </SettingRow>
        {BUILD_CONFIG.isElectron ? (
          <>
            <UpdateCheckSection />
            <SettingRow
              name={t['com.streamlain.aboutAFFiNE.autoCheckUpdate.title']()}
              desc={t[
                'com.streamlain.aboutAFFiNE.autoCheckUpdate.description'
              ]()}
            >
              <Switch checked={autoCheck} onChange={onSwitchAutoCheck} />
            </SettingRow>
            <SettingRow
              name={t['com.streamlain.aboutAFFiNE.autoDownloadUpdate.title']()}
              desc={t[
                'com.streamlain.aboutAFFiNE.autoDownloadUpdate.description'
              ]()}
            >
              <Switch checked={autoDownload} onChange={onSwitchAutoDownload} />
            </SettingRow>
          </>
        ) : null}
        <TelemetryConsentSettings />
      </SettingWrapper>
      <SettingWrapper title={t['com.streamlain.help.menu']()}>
        {[
          {
            label: t['com.streamlain.help.report-bug'](),
            run: () => helpActionsService.reportBug(),
          },
          {
            label: t['com.streamlain.help.submit-idea'](),
            run: () => helpActionsService.submitIdea(),
          },
          {
            label: t['com.streamlain.help.system-status'](),
            run: () =>
              helpActionsService.openStatus(
                t.language?.startsWith('en') ? 'en' : 'de'
              ),
          },
          {
            label: t['com.streamlain.help.release-notes'](),
            run: () => helpActionsService.openReleaseNotes(),
          },
        ].map(action => (
          <SettingRow
            key={action.label}
            name={action.label}
            style={{ cursor: 'pointer' }}
            onClick={action.run}
          >
            <ArrowRightSmallIcon />
          </SettingRow>
        ))}
      </SettingWrapper>
      <SettingWrapper title={t['com.streamlain.aboutAFFiNE.contact.title']()}>
        <a
          className={styles.link}
          rel="noreferrer"
          href="https://streamlain.de"
          target="_blank"
        >
          {t['com.streamlain.aboutAFFiNE.contact.website']()}
          <OpenInNewIcon className="icon" />
        </a>
      </SettingWrapper>
      {relatedLinks.length > 0 ? (
        <SettingWrapper
          title={t['com.streamlain.aboutAFFiNE.community.title']()}
        >
          <div className={styles.communityWrapper}>
            {relatedLinks.map(({ icon, title, link }) => {
              return (
                <div
                  className={styles.communityItem}
                  onClick={() => {
                    urlService.openPopupWindow(link);
                  }}
                  key={title}
                >
                  {icon}
                  <p>{title}</p>
                </div>
              );
            })}
          </div>
        </SettingWrapper>
      ) : null}
      <SettingWrapper title={t['com.streamlain.aboutAFFiNE.legal.title']()}>
        {[
          legalLinks.imprint,
          legalLinks.privacy,
          legalLinks.terms,
          legalLinks.avv,
          legalLinks.withdrawal,
        ].map(link => (
          <a
            className={styles.link}
            rel="noreferrer"
            href={link.url}
            target="_blank"
            key={link.url}
          >
            {link.label}
            <OpenInNewIcon className="icon" />
          </a>
        ))}
      </SettingWrapper>
    </>
  );
};
